import { useState } from 'react';
import { useLocale } from '../../application/i18n/LocaleProvider';
import AppDrawer from './AppDrawer';
import BrandHeader from './BrandHeader';
import LanguageMenu from './LanguageMenu';

/**
 * The header, and the drawer its lockup opens (SPEC-018).
 *
 * The open state belongs to neither of them on its own, and `AppShell` should
 * not have to hold a boolean for a menu — so it lives here, in the one component
 * that renders both.
 *
 * The TRIGGER is the brand lockup rather than a control of its own. A gear
 * beside the language menu was the first shape, and it did not fit: the lockup
 * plus two trailing controls overflowed an iPhone 13 mini by 28px and pushed the
 * wordmark's ".me" behind the language pill. The alternative on the table was
 * shrinking that pill, which spends the whole point of SPEC-001 PR-1.8 — a
 * header that WEARS the language you are reading. The lockup was already there,
 * already the full width of the row, and doing nothing.
 *
 * The drawer is mounted only while open. A `<dialog>` that is always in the tree
 * is a focus-order surprise waiting for someone with a keyboard, and unmounting
 * is what makes `useAppSettings` re-read on every open — which it must, since
 * the whole point of the panel is to report what is true now.
 */
export default function AppHeader({ mark = true }) {
    const { t } = useLocale();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <BrandHeader
                action={<LanguageMenu />}
                /* Forwarded, not defaulted away. `AppShell` passes
                   `chrome.brandMark` and this component sat between the two
                   without carrying it, so `BrandHeader`'s `mark = true` default
                   won everywhere and the landing header drew a lavender the
                   route table had switched off. */
                mark={mark}
                onOpenSettings={() => setIsOpen(true)}
                settingsLabel={t('settings.open')}
            />
            {isOpen ? <AppDrawer onClose={() => setIsOpen(false)} /> : null}
        </>
    );
}
